/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { FormHelperText, Grid, InputLabel, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { generateInvoiceReportApi } from 'api/admin';
import { addMinutes, format, subDays } from 'date-fns';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { Field, Form, useFormState } from 'react-final-form';
import MultiDatePicker from 'react-multi-date-picker';
import { useDispatch } from 'react-redux';
import { handleError } from 'store/thunks/errorHandlerThunks';

const StyledDatePicker = styled(MultiDatePicker)(({ theme }) => ({
  '& .rmdp-week-day': {
    color: theme.palette.primary.main,
    cursor: 'default',
    fontSize: '12px',
    fontWeight: 500,
  },
  '& .rmdp-input:focus': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .rmdp-range': {
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 0 3px #fff',
    color: '#fff',
  },
  '& .rmdp-range-hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day.rmdp-today span': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
    borderColor: '#fff',
  },
  '& .rmdp-day:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day:not(.rmdp-disabled,.rmdp-day-hidden) span:hover': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
  },
  '& .rmdp-arrow-container:hover': {
    backgroundColor: theme.palette.primary.light,
    boxShadow: '0 0 3px #8798ad',
  },
  '& .rmdp-arrow': {
    border: `solid ${theme.palette.primary.light}`,
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    marginTop: '5px',
    padding: '3px',
    width: '3px',
  },
  '&.rmdp-input': {
    padding: '50px',
  },
}));

const CustomDateRangeField = () => {
  const theme = useTheme();
  const { submitting } = useFormState();
  const maxDate = useMemo(() => addMinutes(new Date(), 60), []);
  const minDate = useMemo(() => subDays(new Date(), 29), []);

  return (
    <Field
      name='dateRange'
      id='NdcFillHistory-dateRange-input'
      validate={(value) => {
        if (isEmpty(value) || value?.length < 2) {
          return 'Required';
        }

        return undefined;
      }}
      render={({ input, meta }) => {
        // showError needs to be a boolean, material type check this value
        const showError = !!meta.error && (!!meta.dirty || !!meta.touched);
        const displayedHelperText = showError && meta.error;

        return (
          <>
            <InputLabel
              htmlFor='GenerateInvoiceReport-dateRange-input'
              sx={{ fontSize: '1em', mt: -0.5 }}
              error={showError}
              required
              disabled={submitting}
            >
              Date Range
            </InputLabel>
            <StyledDatePicker
              id='NdcFillHistory-dateRange-input'
              name='dateRange'
              placeholder='MM/DD/YYYY ~ MM/DD/YYYY'
              format='MM/DD/YYYY'
              required
              maxDate={maxDate}
              minDate={minDate}
              highlightToday={false}
              editable={false}
              disabled={submitting}
              style={{
                width: '100%',
                minWidth: 210,
                height: '40px',
                padding: 10,
                margin: 0,
                marginBottom: 0,
                marginTop: 0,
                border: '1px solid',
                borderColor: showError ? theme.palette.error.main : theme.palette.grey[400],
                background: 'inherit',
                borderRadius: '3px',
              }}
              calendarPosition='bottom-center'
              range
              rangeHover
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...input} // intentional spreading, injecting final-form
              onChange={(data) => {
                input.onChange(data);
              }}
            />
            <FormHelperText sx={{ color: theme.palette.error.main, pl: 1, mb: 0, mt: 0.5 }}>
              {displayedHelperText}
            </FormHelperText>
          </>
        );
      }}
    />
  );
};

const GenerateInvoices = () => {
  const dispatch = useDispatch();
  const [inProgress, setInprogress] = useState(true);

  const handleGenerateReports = ({ dateRange }) => {
    enqueueSnackbar({
      message: 'Generating Invoice Reports. Please check back later.',
      variant: 'info',
    });

    return generateInvoiceReportApi({
      startDate: format(new Date(dateRange[0]), 'yyyy-MM-dd'),
      endDate: format(new Date(dateRange[1]), 'yyyy-MM-dd'),
    })
      .then(() => {
        localStorage.setItem('invoiceGenerationTimestamp', Date.now());
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const currentTime = Date.now();
      const lastGenerated = parseInt(
        localStorage.getItem('invoiceGenerationTimestamp') || currentTime,
        10
      );

      setInprogress(currentTime - lastGenerated < 600_000);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Grid container sx={{ p: 2 }} rowSpacing={2}>
      <Grid item xs={12}>
        <Typography variant='h6' component='h1'>
          Generate Invoices
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Form
          onSubmit={handleGenerateReports}
          render={({ handleSubmit, submitting, invalid }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container>
                <Grid item>
                  <CustomDateRangeField />
                </Grid>
                <Grid item sx={{ mt: 2, mx: 2 }}>
                  <LoadingButton
                    loading={submitting}
                    variant='contained'
                    type='submit'
                    size='large'
                    disabled={invalid || inProgress}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
                {inProgress && (
                  <Grid item sx={{ mt: 2.5 }}>
                    <Typography variant='h6' component='h2' color='primary'>
                      In Progress, please try again later.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
        />
      </Grid>
    </Grid>
  );
};
export default GenerateInvoices;
